import {
  projectMembershipSeatAuthority,
  type MembershipPlanCapacity,
  type MembershipSeatAuthorityReport,
} from './MembershipSeatAuthority';

export interface MembershipDashboardCounterPayload {
  generated_at?: string;
  owner_user_id?: string;
  plan?: string;

  plan_capacity?: number;
  owner_seat_count?: number;
  active_sponsored_seat_count?: number;
  reserved_sponsored_seat_count?: number;
  occupied_seat_count?: number;
  available_sponsored_seat_count?: number;
  returned_sponsored_seat_count?: number;

  family_invites_sent?: number;
  family_invites_pending?: number;
  invites_accepted?: number;
  invites_expired?: number;
  total_invites?: number;

  claimed_family_profiles?: number;
  unclaimed_family_profiles?: number;

  family_invites_unlimited?: boolean;
  sponsored_reservation_days?: number;
}

export interface MembershipInvitationCounters {
  totalInvites: number;
  familyInvitesSent: number;
  familyInvitesPending: number;
  acceptedInvites: number;
  expiredInvites: number;
  returnedSponsoredSeats: number;
}

export interface MembershipFamilyProfileCounters {
  claimedFamilyProfiles: number;
  unclaimedFamilyProfiles: number;
  totalFamilyProfiles: number;
}

export interface MembershipDashboardCounterAuthority {
  status: 'PASS' | 'INVALID';
  evidenceSource: 'live-database-rpc';
  rpc: 'get_membership_dashboard_counters';

  seatReport: MembershipSeatAuthorityReport;
  invitations: MembershipInvitationCounters;
  familyProfiles: MembershipFamilyProfileCounters;

  familyInvitesUnlimited: true;
  sponsoredReservationDays: 7;
  generatedAt: string;
  ownerUserId: string | null;
}

function nonNegativeInteger(
  value: unknown,
): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0
    ? value
    : 0;
}

function resolveCapacity(
  value: unknown,
): MembershipPlanCapacity {
  return value === 5 ||
    value === 10 ||
    value === 30
    ? value
    : 1;
}

export function projectMembershipDashboardCounters(
  payload: MembershipDashboardCounterPayload,
): MembershipDashboardCounterAuthority {
  const planCapacity = resolveCapacity(
    payload.plan_capacity,
  );

  const activeSponsoredSeats =
    nonNegativeInteger(
      payload.active_sponsored_seat_count,
    );

  const reservedSponsoredSeats =
    nonNegativeInteger(
      payload.reserved_sponsored_seat_count,
    );

  const returnedSponsoredSeats =
    nonNegativeInteger(
      payload.returned_sponsored_seat_count,
    );

  const familyInvitesSent =
    nonNegativeInteger(
      payload.family_invites_sent,
    );

  const familyInvitesPending =
    nonNegativeInteger(
      payload.family_invites_pending,
    );

  const seatReport =
    projectMembershipSeatAuthority({
      planCapacity,
      activeSponsoredSeats,
      reservedSponsoredSeats,
      returnedSeatCount:
        returnedSponsoredSeats,
      familyInvitesSent,
      familyInvitesPending,
    });

  const claimedFamilyProfiles =
    nonNegativeInteger(
      payload.claimed_family_profiles,
    );

  const unclaimedFamilyProfiles =
    nonNegativeInteger(
      payload.unclaimed_family_profiles,
    );

  const valid =
    payload.owner_seat_count === 1 &&
    payload.family_invites_unlimited === true &&
    payload.sponsored_reservation_days === 7 &&
    seatReport.status !== 'INVALID';

  return Object.freeze({
    status: valid ? 'PASS' : 'INVALID',
    evidenceSource: 'live-database-rpc',
    rpc: 'get_membership_dashboard_counters',

    seatReport,

    invitations: Object.freeze({
      totalInvites:
        nonNegativeInteger(
          payload.total_invites,
        ),
      familyInvitesSent,
      familyInvitesPending,
      acceptedInvites:
        nonNegativeInteger(
          payload.invites_accepted,
        ),
      expiredInvites:
        nonNegativeInteger(
          payload.invites_expired,
        ),
      returnedSponsoredSeats,
    }),

    familyProfiles: Object.freeze({
      claimedFamilyProfiles,
      unclaimedFamilyProfiles,
      totalFamilyProfiles:
        claimedFamilyProfiles +
        unclaimedFamilyProfiles,
    }),

    familyInvitesUnlimited: true,
    sponsoredReservationDays: 7,
    generatedAt:
      payload.generated_at ??
      new Date().toISOString(),
    ownerUserId:
      payload.owner_user_id ?? null,
  });
}

export const MEMBERSHIP_DASHBOARD_COUNTER_AUTHORITY_READY =
  true;
